/**
 * Hook para captura de tela via WebRTC
 * Gerencia getDisplayMedia e estado do stream
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import type { UseScreenShareReturn } from './types';
import { DISPLAY_MEDIA_CONSTRAINTS } from './types';

// Logger para diagnóstico
const logger = createLogger('useScreenShare');

/**
 * Hook para captura de tela do usuário
 * 
 * Responsabilidades:
 * - Solicitar permissão de captura via navigator.mediaDevices.getDisplayMedia
 * - Gerenciar estado do stream (ativo, encerrado)
 * - Configurar constraints otimizados para suporte remoto
 * - Cleanup automático ao desmontar
 * 
 * @returns Objeto com stream, estado e funções de controle
 */
export function useScreenShare(): UseScreenShareReturn {
  // Estado do stream de captura
  const [stream, setStream] = useState<MediaStream | null>(null);
  // Indica se está compartilhando ativamente
  const [isSharing, setIsSharing] = useState(false);
  // Mensagem de erro, se houver
  const [error, setError] = useState<string | null>(null);
  
  // Ref para garantir cleanup adequado
  const streamRef = useRef<MediaStream | null>(null);

  /**
   * Para todas as tracks do stream e libera recursos
   */
  const stopAllTracks = useCallback((mediaStream: MediaStream | null) => {
    if (!mediaStream) return;
    
    logger.debug('Parando todas as tracks do stream');
    mediaStream.getTracks().forEach(track => {
      track.stop();
      logger.debug(`Track ${track.kind} parada`);
    });
  }, []);

  /**
   * Inicia captura de tela
   * Abre dialog nativo do navegador para seleção de tela/janela
   * 
   * @returns MediaStream se captura iniciou com sucesso, null caso contrário
   */
  const startSharing = useCallback(async (): Promise<MediaStream | null> => {
    // Limpa erro anterior
    setError(null);

    // Verifica suporte do navegador
    if (!navigator.mediaDevices?.getDisplayMedia) {
      const errorMsg = 'Navegador não suporta compartilhamento de tela';
      logger.error(errorMsg);
      setError(errorMsg);
      return null;
    }

    try {
      logger.info('Iniciando captura de tela...');
      
      // Solicita captura de tela ao navegador
      // Abre dialog nativo para o usuário selecionar tela/janela
      const mediaStream = await navigator.mediaDevices.getDisplayMedia(DISPLAY_MEDIA_CONSTRAINTS);
      
      logger.info('Captura de tela iniciada com sucesso', {
        videoTracks: mediaStream.getVideoTracks().length,
      });

      // Configura listener para quando usuário clicar em "Parar compartilhamento"
      // Isso acontece quando usuário usa o botão nativo do navegador
      mediaStream.getVideoTracks().forEach(track => {
        track.onended = () => {
          logger.info('Usuário encerrou compartilhamento via controle nativo');
          setStream(null);
          setIsSharing(false);
          streamRef.current = null;
        };
      });

      // Atualiza estado
      streamRef.current = mediaStream;
      setStream(mediaStream);
      setIsSharing(true);
      
      // Retorna o stream diretamente para evitar race condition com estado React
      return mediaStream;
    } catch (err) {
      // Trata erros específicos
      let errorMsg = 'Erro ao iniciar compartilhamento de tela';
      
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          // Usuário negou permissão ou cancelou dialog
          errorMsg = 'Permissão negada pelo usuário';
          logger.info('Usuário cancelou/negou compartilhamento');
        } else if (err.name === 'NotFoundError') {
          errorMsg = 'Nenhuma fonte de captura encontrada';
          logger.warn('Nenhuma fonte de captura disponível');
        } else if (err.name === 'NotSupportedError') {
          errorMsg = 'Compartilhamento de tela não suportado neste navegador';
          logger.error('Navegador não suporta getDisplayMedia');
        } else {
          logger.error('Erro desconhecido ao capturar tela', err);
        }
      }
      
      setError(errorMsg);
      return null;
    }
  }, []);

  /**
   * Para captura de tela e libera recursos
   */
  const stopSharing = useCallback(() => {
    logger.info('Parando compartilhamento de tela');
    
    stopAllTracks(streamRef.current);
    streamRef.current = null;
    setStream(null);
    setIsSharing(false);
    setError(null);
  }, [stopAllTracks]);

  /**
   * Cleanup ao desmontar componente
   * Garante que recursos são liberados mesmo se componente for desmontado abruptamente
   */
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        logger.debug('Cleanup: liberando stream ao desmontar');
        stopAllTracks(streamRef.current);
      }
    };
  }, [stopAllTracks]);

  return {
    stream,
    isSharing,
    error,
    startSharing,
    stopSharing,
  };
}
